/**
 * Display Mode Context - E-Ink Mode Support
 *
 * Manages display mode preference (normal vs e-ink) for the application.
 * - Reads tenant config to check if eink_mode feature is enabled
 * - Persists user preference in localStorage (pre-auth) and syncs to backend (post-auth)
 * - Applies body.eink-mode class when enabled
 *
 * Usage:
 *   const { isEInkMode, setEInkMode, isFeatureEnabled } = useDisplayMode();
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useTenant } from './TenantContext';

// ============================================================
// Constants
// ============================================================

const STORAGE_KEY = 'ngui_eink_mode';

// ============================================================
// Types
// ============================================================

interface DisplayModeContextType {
  /** Whether E-Ink mode is currently enabled */
  isEInkMode: boolean;
  /** Toggle E-Ink mode on/off */
  setEInkMode: (enabled: boolean) => void;
  /** Whether the tenant has E-Ink mode feature enabled */
  isFeatureEnabled: boolean;
}

// ============================================================
// Context
// ============================================================

const DisplayModeContext = createContext<DisplayModeContextType>({
  isEInkMode: false,
  setEInkMode: () => {},
  isFeatureEnabled: false,
});

// ============================================================
// Provider
// ============================================================

interface DisplayModeProviderProps {
  children: ReactNode;
}

export function DisplayModeProvider({ children }: DisplayModeProviderProps): JSX.Element {
  const { tenant } = useTenant();

  // Check if tenant has E-Ink mode feature enabled
  const isFeatureEnabled = tenant?.einkModeEnabled ?? false;

  // Initialize from localStorage
  const [isEInkMode, setIsEInkModeState] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'true';
  });

  // Apply/remove body class when mode changes
  useEffect(() => {
    if (isEInkMode && isFeatureEnabled) {
      document.body.classList.add('eink-mode');
    } else {
      document.body.classList.remove('eink-mode');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('eink-mode');
    };
  }, [isEInkMode, isFeatureEnabled]);

  // If feature is disabled by tenant, ensure E-Ink mode is off
  useEffect(() => {
    if (!isFeatureEnabled && isEInkMode) {
      // Don't change stored preference, just don't apply the mode
      document.body.classList.remove('eink-mode');
    }
  }, [isFeatureEnabled, isEInkMode]);

  // Set E-Ink mode with persistence
  const setEInkMode = useCallback((enabled: boolean) => {
    setIsEInkModeState(enabled);
    localStorage.setItem(STORAGE_KEY, String(enabled));
    // Future enhancement: sync to user preferences via API when authenticated
  }, []);

  return (
    <DisplayModeContext.Provider
      value={{
        isEInkMode: isEInkMode && isFeatureEnabled,
        setEInkMode,
        isFeatureEnabled,
      }}
    >
      {children}
    </DisplayModeContext.Provider>
  );
}

// ============================================================
// Hook
// ============================================================

export function useDisplayMode(): DisplayModeContextType {
  const context = useContext(DisplayModeContext);
  if (context === undefined) {
    throw new Error('useDisplayMode must be used within a DisplayModeProvider');
  }
  return context;
}

// ============================================================
// Utility Hook - Check E-Ink Mode (shorthand)
// ============================================================

/**
 * Shorthand hook to just check if E-Ink mode is active
 */
export function useIsEInkMode(): boolean {
  const { isEInkMode } = useDisplayMode();
  return isEInkMode;
}
